import { ReactNode, Suspense, useEffect, useRef, useState } from 'react';

/**
 * Below-the-fold landing section (issue #236, F27): its code and media are requested when
 * it comes near the viewport or the browser is idle — not together with the hero. The
 * placeholder keeps the expected height so nothing jumps when it renders.
 */
const REVEAL_EVENT = 'trenio:reveal-sections';
let revealedAll = false;

/** Mount every lazy section now (anchor navigation, #hash on load). */
export const revealLazySections = () => {
    revealedAll = true;
    window.dispatchEvent(new Event(REVEAL_EVENT));
};

/** Scrolls to `#id` once it exists (lazy sections may still be loading). */
export const scrollToSection = (id: string, timeoutMs = 4000) => {
    revealLazySections();
    const started = Date.now();
    const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        if (Date.now() - started < timeoutMs) window.setTimeout(attempt, 50);
    };
    attempt();
};

interface LazySectionProps {
    children: ReactNode;
    /** Reserved height until the section renders (px). */
    minHeight?: number;
}

export function LazySection({ children, minHeight = 600 }: LazySectionProps) {
    const [visible, setVisible] = useState(revealedAll);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (visible) return;
        const show = () => setVisible(true);
        window.addEventListener(REVEAL_EVENT, show);

        let observer: IntersectionObserver | null = null;
        if (ref.current && 'IntersectionObserver' in window) {
            observer = new IntersectionObserver((entries) => {
                if (entries.some((e) => e.isIntersecting)) show();
            }, { rootMargin: '800px 0px' });
            observer.observe(ref.current);
        } else {
            show();
        }

        // Idle fallback so the page is complete for keyboard/find-in-page users and crawlers
        const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, o?: { timeout: number }) => number);
        const idleId = ric ? ric(show, { timeout: 8000 }) : window.setTimeout(show, 4000);

        return () => {
            window.removeEventListener(REVEAL_EVENT, show);
            observer?.disconnect();
            const cic = (window as any).cancelIdleCallback as undefined | ((id: number) => void);
            if (ric && cic) cic(idleId); else window.clearTimeout(idleId);
        };
    }, [visible]);

    const placeholder = <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
    if (!visible) return placeholder;
    return <Suspense fallback={<div style={{ minHeight }} aria-hidden="true" />}>{children}</Suspense>;
}
